import { AsyncPipe, NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, HostBinding, NgModule, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { FormFieldModule } from '../../wrapper/form-field/form-field.component';
import { CyTestDirective } from '@portal/directive';
import { PortalFieldTypeConfig } from '@portal/form/data-access';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_SELECT_SCROLL_STRATEGY, MatSelect, MatSelectModule } from '@angular/material/select';
import { FormlySelectModule, FormlySelectOption, FormlySelectOptionsPipe } from '@ngx-formly/core/select';
import { BlockScrollStrategy, Overlay } from '@angular/cdk/overlay';
import { isObservable, map, take } from 'rxjs';
import { EMPTY_VALUE } from '@core/data-access';

function scrollFactory(overlay: Overlay): () => BlockScrollStrategy {
  return () => overlay.scrollStrategies.block();
}

@Component({
  selector: 'portal-form-mat-select',
  standalone: true,
  imports: [
    NgIf,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    AsyncPipe,
    NgForOf,
    AsyncPipe,
    FormlyModule,
    FormlySelectModule,
    MatSelectModule,
    ReactiveFormsModule,
    CyTestDirective,
    MatCheckboxModule,
  ],
  providers: [
    {
      provide: MAT_SELECT_SCROLL_STRATEGY,
      useFactory: scrollFactory,
      deps: [Overlay],
    },
    FormlySelectOptionsPipe,
  ],
  templateUrl: './mat-select.component.html',
  styleUrls: ['./mat-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MatSelectComponent extends FieldType<PortalFieldTypeConfig> implements AfterViewInit {
  @ViewChild(MatSelect) private readonly matSelect!: MatSelect;

  protected readonly defaultValue = EMPTY_VALUE;

  get isObservableValue() {
    return isObservable(this.value);
  }

  @HostBinding('class') get hostClass(): string {
    return this.disabled ? 'field-value' : 'field-input-wrapper';
  }

  /**
   * Check if form or field is disabled
   * @returns boolean
   * @readonly
   */
  get disabled(): boolean {
    return !!this.options?.formState?.disabled || !!this.props?.disabled;
  }

  get value() {
    if (this.disabled) {
      if (isObservable(this.props.options)) {
        return this.props.options?.pipe(
          take(1),
          map(options => {
            const option = options.find(option => option.value === this.formControl.value);
            return option?.label;
          }),
        );
      } else {
        const option = this.props.options?.find(option => option.value === this.formControl.value);
        return option?.label;
      }
    }
    return null;
  }

  ngAfterViewInit(): void {
    const element = this.matSelect._elementRef.nativeElement;

    element.addEventListener('mouseenter', () => {
      element.title = this.matSelect.triggerValue;
    });
  }

  compareFn(c1: unknown, c2: unknown): boolean {
    return c1 === c2;
  }

  getObsValue(options: FormlySelectOption[]) {
    return this.props.multiple ? this.renderMultiValue(options) : this.renderSingleValue(options);
  }

  renderViewValue() {
    if (this.props.displayWith) {
      return this.displayValue(this.formControl.value) || this.defaultValue;
    }

    if (Array.isArray(this.props.options)) {
      return this.props.multiple ? this.renderMultiValue(this.props.options) : this.renderSingleValue(this.props.options);
    }

    return this.displayValue(this.formControl.value);
  }

  private renderMultiValue(options: FormlySelectOption[]): string {
    const value = this.formControl.value;
    if (!Array.isArray(value)) return this.displayValue(value);

    return options
      .filter(option => value.includes(option.value))
      .map(option => this.displayValue(option.label))
      .join(', ');
  }

  private renderSingleValue(options: FormlySelectOption[]): string {
    const value = this.formControl.value;
    const optionValue = options.find(option => option.value === value)?.label;
    return this.displayValue(optionValue || value);
  }

  private displayValue(value: any) {
    return this.props.displayWith?.(value) || value || this.defaultValue;
  }
}

@NgModule({
  imports: [
    FormFieldModule,
    FormlyModule.forChild({
      types: [{ name: 'mat-select', component: MatSelectComponent, wrappers: ['form-field'] }],
    }),
  ],
})
export class FormMatSelectModule {}
